import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

import { count } from '../manage/manage';
import { CRM_TAG_CHOICES } from '../tagging/tagging.queries';
import { useDebounced } from '../tagging/useDebounced';
import { CRM_TEST_CAMPAIGN_CHOICES } from '../email/email.queries';
import { CRM_DRAFT_CAMPAIGN_CHOICES, CRM_SEGMENT_CHOICES } from '../workflows/workflows.queries';

/**
 * The pickers the email composer and the workflow editor share: a tag, tags, a
 * list or saved filter, a draft campaign. Each asks for its choices only when
 * it is on screen, and lists and campaigns only as typed (there are thousands).
 */

export interface TagChoice {
  id: number;
  name: string;
  color: string | null;
  isSystem: boolean;
  contactCount: number;
}

const Dot = ({ color }: { color?: string | null }) => (
  <Box component="span" aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color || '#94a3b8', flexShrink: 0 }} />
);

export const useTags = () => {
  const { data, loading } = useQuery<{ crmTags: Array<TagChoice> }>(CRM_TAG_CHOICES);
  return { tags: data?.crmTags ?? [], loading };
};

interface TagSelectProps {
  label: string;
  value: number | null;
  /** The name the API gave with the id, shown until the tags load. */
  valueName?: string | null;
  onChange: (tag: { id: number; name: string } | null) => void;
  error?: string;
  disabled?: boolean;
}

export const TagSelect = ({ label, value, valueName, onChange, error, disabled }: TagSelectProps) => {
  const { tags, loading } = useTags();
  const selected = tags.find((t) => t.id === value) ?? (value ? ({ id: value, name: valueName ?? `#${value}`, color: null, isSystem: false, contactCount: 0 } as TagChoice) : null);

  return (
    <Autocomplete
      options={tags}
      value={selected}
      loading={loading}
      disabled={disabled}
      onChange={(_, tag) => onChange(tag ? { id: tag.id, name: tag.name } : null)}
      getOptionLabel={(tag) => tag.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, tag) => (
        <li {...props} key={tag.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Dot color={tag.color} />
            <Box sx={{ flex: 1, minWidth: 0 }}>{tag.name}</Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {count(tag.contactCount)}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} error={!!error} helperText={error} />}
    />
  );
};

interface TagsSelectProps {
  label: string;
  value: Array<number>;
  onChange: (ids: Array<number>) => void;
  max: number;
  helperText?: string;
  error?: string;
  disabled?: boolean;
}

export const TagsSelect = ({ label, value, onChange, max, helperText, error, disabled }: TagsSelectProps) => {
  const { tags, loading } = useTags();
  const selected = value.map((id) => tags.find((t) => t.id === id) ?? ({ id, name: `#${id}`, color: null, isSystem: false, contactCount: 0 } as TagChoice));

  return (
    <Autocomplete
      multiple
      options={tags}
      value={selected}
      loading={loading}
      disabled={disabled}
      getOptionDisabled={() => value.length >= max}
      onChange={(_, next) => onChange(next.map((t) => t.id))}
      getOptionLabel={(tag) => tag.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderTags={(chosen, getTagProps) =>
        chosen.map((tag, index) => (
          <Chip {...getTagProps({ index })} key={tag.id} size="small" label={tag.name} icon={<Box sx={{ pl: 0.75, display: 'flex' }}><Dot color={tag.color} /></Box>} />
        ))
      }
      renderInput={(params) => <TextField {...params} label={label} error={!!error} helperText={error ?? helperText} />}
    />
  );
};

/* ------------------------------------------------------------------ *
 * Lists and saved filters
 * ------------------------------------------------------------------ */

export interface SegmentChoice {
  id: number;
  name: string;
  type?: string;
  isActive?: boolean;
  contactCount?: number;
}

interface SegmentSelectProps {
  label: string;
  value: SegmentChoice | null;
  onChange: (segment: SegmentChoice | null) => void;
  error?: string;
  helperText?: string;
  disabled?: boolean;
}

/** A list or a saved filter, searched by name as typed. Inactive NationBuilder filters cannot be chosen: nothing can send to them. */
export const SegmentSelect = ({ label, value, onChange, error, helperText, disabled }: SegmentSelectProps) => {
  const [typed, setTyped] = useState('');
  const search = useDebounced(typed.trim().slice(0, 200));
  const { data, loading } = useQuery<{ crmSegments: { items: Array<SegmentChoice> } }>(CRM_SEGMENT_CHOICES, {
    variables: { search: search || null },
  });
  const options = data?.crmSegments.items ?? [];

  return (
    <Autocomplete
      options={options}
      value={value}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      onInputChange={(_, text, reason) => reason === 'input' && setTyped(text)}
      onChange={(_, segment) => onChange(segment)}
      getOptionLabel={(segment) => segment.name}
      getOptionDisabled={(segment) => segment.isActive === false}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, segment) => (
        <li {...props} key={segment.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            {segment.type === 'DYNAMIC' ? (
              <FilterAltOutlinedIcon fontSize="small" sx={{ color: '#6a1b9a' }} />
            ) : (
              <ListAltOutlinedIcon fontSize="small" sx={{ color: '#2e7d32' }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {segment.name}
              {segment.isActive === false && (
                <Typography variant="caption" component="div" color="warning.dark">
                  Not re-expressed since NationBuilder
                </Typography>
              )}
            </Box>
            {segment.contactCount !== undefined && (
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {count(segment.contactCount)}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Type to find a list or saved filter" error={!!error} helperText={error ?? helperText} />
      )}
    />
  );
};

/* ------------------------------------------------------------------ *
 * Draft campaigns, for an email step
 * ------------------------------------------------------------------ */

export interface CampaignChoice {
  id: number;
  name: string;
  subject: string | null;
  workflowStepCount?: number;
}

interface CampaignSelectProps {
  value: CampaignChoice | null;
  onChange: (campaign: CampaignChoice | null) => void;
  error?: string;
  disabled?: boolean;
  /** Every one of ours, not only drafts: for sending a test of any. */
  anyStatus?: boolean;
}

export const CampaignSelect = ({ value, onChange, error, disabled, anyStatus }: CampaignSelectProps) => {
  const [typed, setTyped] = useState('');
  const search = useDebounced(typed.trim().slice(0, 200));
  const { data, loading } = useQuery<{ crmEmailCampaigns: { items: Array<CampaignChoice> } }>(anyStatus ? CRM_TEST_CAMPAIGN_CHOICES : CRM_DRAFT_CAMPAIGN_CHOICES, {
    variables: { search: search || null },
    fetchPolicy: 'cache-and-network',
  });

  return (
    <Autocomplete
      options={data?.crmEmailCampaigns.items ?? []}
      value={value}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      onInputChange={(_, text, reason) => reason === 'input' && setTyped(text)}
      onChange={(_, campaign) => onChange(campaign)}
      getOptionLabel={(campaign) => campaign.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, campaign) => (
        <li {...props} key={campaign.id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {campaign.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" noWrap>
              {campaign.subject || 'No subject yet'}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={anyStatus ? 'Campaign' : 'Email'} placeholder={anyStatus ? 'Find a campaign' : 'Find a draft campaign'} error={!!error} helperText={error} />}
    />
  );
};
